import { useMemo } from "react";
import { Select } from "../shared";
import { useGetEquipmentQuery } from "../redux/services/equipment";
import { buildFleetEquipmentSelectOptions } from "../utils/equipmentAvailability";
import { removeEmptyValues } from "../utils";

/**
 * Truck/trailer pickers for trucking job legs (parity with Dispatch quick edit).
 *
 * @param {object} props
 * @param {string} props.size - Select size
 * @param {string|null} props.powerUnitId - Selected power unit id
 * @param {string|null} props.trailerId - Selected trailer id
 * @param {function} props.onPowerUnitChange - (id|null) => void
 * @param {function} props.onTrailerChange - (id|null) => void
 * @param {boolean} [props.availableOnly] - Only list available units from API
 * @param {string} [props.tags] - Comma-separated tag ids filter
 */
export function LegFleetEquipmentFields({
  size = "md",
  powerUnitId = null,
  trailerId = null,
  onPowerUnitChange,
  onTrailerChange,
  availableOnly = true,
  tags,
}) {
  const { data: trucksData } = useGetEquipmentQuery(
    removeEmptyValues({
      page: 1,
      limit: 200,
      equipmentCategory: "truck",
      tags,
    })
  );
  const { data: trailersData } = useGetEquipmentQuery(
    removeEmptyValues({
      page: 1,
      limit: 200,
      equipmentCategory: "trailer",
      tags,
    })
  );

  const truckOptions = useMemo(
    () =>
      buildFleetEquipmentSelectOptions(trucksData?.equipment, powerUnitId, {
        availableOnly,
      }),
    [trucksData, powerUnitId, availableOnly]
  );

  const trailerOptions = useMemo(
    () =>
      buildFleetEquipmentSelectOptions(trailersData?.equipment, trailerId, {
        availableOnly,
      }),
    [trailersData, trailerId, availableOnly]
  );

  const powerUnitLabel =
    truckOptions.find((o) => String(o.value) === String(powerUnitId))?.label ||
    "";
  const trailerLabel =
    trailerOptions.find((o) => String(o.value) === String(trailerId))?.label ||
    "";

  return (
    <>
      <Select
        size={size}
        id="leg-power-unit"
        value={powerUnitLabel}
        onChange={(selected) =>
          onPowerUnitChange(selected?.value || null)
        }
        options={truckOptions}
        placeholder="Power unit (truck)"
        label="Power unit"
      />
      <Select
        size={size}
        id="leg-trailer"
        value={trailerLabel}
        onChange={(selected) => onTrailerChange(selected?.value || null)}
        options={trailerOptions}
        placeholder="Trailer (optional)"
        label="Trailer"
      />
    </>
  );
}

/**
 * @param {object|undefined|null} unit - Populated equipment or id
 * @returns {string}
 */
export function formatFleetUnitLabel(unit) {
  if (!unit) return "";
  if (typeof unit === "string") return "";
  return unit.name || unit.licensePlate || "";
}

/**
 * @param {object} leg
 * @returns {string}
 */
export function formatLegFleetSummary(leg) {
  const parts = [
    formatFleetUnitLabel(leg?.powerUnit),
    formatFleetUnitLabel(leg?.trailer),
  ].filter(Boolean);
  return parts.length ? parts.join(" · ") : "";
}
